import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { API_BASE_URL } from '../services/api';

function formatDate(val) {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString('en-GB');
  } catch {
    return String(val);
  }
}

export default function TbillSellDealModal({
  show,
  onClose,
  onConfirm,
  onPortfolioChange,
  isin,
  tbillFaceValue,
  pricePer100,
  formValueDate
}) {
  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState('');
  const [deals, setDeals] = useState([]);
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAmountWarning, setShowAmountWarning] = useState(false);
  const [amountDifference, setAmountDifference] = useState(0);
  const [balanceValidation, setBalanceValidation] = useState({
    isValid: true,
    message: '',
    validationResults: []
  });

  useEffect(() => {
    if (!show) return;
    axios
      .get(`${API_BASE_URL}/portfolio-master`)
      .then((res) => {
        const rows = res.data || [];
        const mapped = rows.map((p) => ({
          id: p.portfolio_id,
          name: p.portfolio_name
        }));
        setPortfolios(mapped);
        const sherwood = mapped.find((p) => String(p.name || '').toLowerCase().includes('sherwood'));
        if (sherwood) {
          setSelectedPortfolio((prev) => {
            if (prev) return prev;
            if (onPortfolioChange) onPortfolioChange(sherwood.id);
            return sherwood.id;
          });
        }
      })
      .catch(() => setError('Failed to load portfolios'));
  }, [show]);

  useEffect(() => {
    if (!show) {
      setSelectedPortfolio('');
      setDeals([]);
      setSelectedDeals([]);
      setSearch('');
      setError('');
      setShowAmountWarning(false);
      setAmountDifference(0);
      setBalanceValidation({ isValid: true, message: '', validationResults: [] });
    }
  }, [show]);

  useEffect(() => {
    if (!selectedPortfolio) {
      setDeals([]);
      setSelectedDeals([]);
      return;
    }
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/tbill/buy-deals?isin=${encodeURIComponent(isin || '')}&portfolio=${encodeURIComponent(selectedPortfolio)}`)
      .then((res) => {
        const rows = res.data?.data || res.data || [];
        setDeals(Array.isArray(rows) ? rows : []);
        setSelectedDeals([]);
      })
      .catch(() => setError('Failed to fetch T-Bill holdings'))
      .finally(() => setLoading(false));
  }, [selectedPortfolio, isin]);

  const filteredDeals = useMemo(() => {
    let rows = deals;
    if (formValueDate) {
      rows = rows.filter((d) => {
        if (!d.value_date) return true;
        try {
          return new Date(d.value_date) <= new Date(formValueDate);
        } catch {
          return true;
        }
      });
    }
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (d) =>
        String(d.deal_number || '').toLowerCase().includes(q) ||
        String(d.yield ?? d.discount_rate_pct ?? '').toLowerCase().includes(q)
    );
  }, [deals, search, formValueDate]);

  const validateBalanceForDeals = (rows) => {
    const validRows = rows.filter((d) => d.amountToSell && Number(d.amountToSell) > 0);
    if (!validRows.length) {
      setBalanceValidation({ isValid: true, message: 'No amounts to validate', validationResults: [] });
      return;
    }
    const availableByIsin = {};
    (deals || []).forEach((d) => {
      const key = d.isin || isin;
      if (!key) return;
      const remaining = Number(d.remaining_face_value ?? d.face_value ?? 0) || 0;
      availableByIsin[key] = (availableByIsin[key] || 0) + remaining;
    });
    const requestedByIsin = {};
    validRows.forEach((d) => {
      const dealRow = deals.find((x) => x.id === d.dealId);
      const key = dealRow?.isin || isin;
      const requested = Number(d.amountToSell || 0) || 0;
      requestedByIsin[key] = (requestedByIsin[key] || 0) + requested;
    });
    const validationResults = Object.keys(requestedByIsin).map((key) => {
      const availableBalance = Number(availableByIsin[key] || 0);
      const requestedAmount = Number(requestedByIsin[key] || 0);
      const isValid = requestedAmount <= availableBalance + 0.0001;
      return {
        isin: key,
        isValid,
        availableBalance,
        requestedAmount
      };
    });
    const hasErrors = validationResults.some((r) => !r.isValid);
    setBalanceValidation({
      isValid: !hasErrors,
      message: hasErrors ? 'Some deals exceed available balance' : 'All deals validated successfully',
      validationResults
    });
  };

  const handlePortfolioChange = (portfolio) => {
    setSelectedPortfolio(portfolio);
    if (onPortfolioChange) onPortfolioChange(portfolio);
  };

  const handleDealSelect = (dealId) => {
    setSelectedDeals((prev) => {
      const next = prev.some((d) => d.dealId === dealId)
        ? prev.filter((d) => d.dealId !== dealId)
        : [...prev, { dealId, amountToSell: '' }];
      validateBalanceForDeals(next);
      return next;
    });
  };

  const handleAmountChange = (dealId, value, maxValue) => {
    let v = value.replace(/[^0-9.]/g, '');
    if (Number(v) > Number(maxValue)) v = String(maxValue);
    setSelectedDeals((prev) => {
      const next = prev.map((d) => (d.dealId === dealId ? { ...d, amountToSell: v } : d));
      validateBalanceForDeals(next);
      return next;
    });
  };

  const buildConfirmedDeals = (valid) =>
    valid.map((d) => {
      // Use Number() to guard against string/number type mismatch from MySQL vs option values
      const deal = deals.find((x) => Number(x.id) === Number(d.dealId));
      const sold = Number(d.amountToSell) || 0;
      return {
        ...deal,
        amountToSell: sold,
        buy_deal_number: deal?.deal_number,
        buy_deal_id: deal?.id  // numeric fallback if deal_number is null
      };
    });

  const checkAmountDifference = () => {
    const target = Number(tbillFaceValue) || 0;
    const totalSellAmount = selectedDeals.reduce((sum, d) => sum + (Number(d.amountToSell) || 0), 0);
    const difference = Math.abs(target - totalSellAmount);
    if (difference > 0.01) {
      setAmountDifference(difference);
      setShowAmountWarning(true);
      return false;
    }
    return true;
  };

  const finishConfirm = (updateFaceValue) => {
    const valid = selectedDeals.filter((d) => d.amountToSell && Number(d.amountToSell) > 0);
    onConfirm({
      deals: buildConfirmedDeals(valid),
      portfolio: selectedPortfolio,
      updateFaceValue
    });
  };

  const handleConfirm = () => {
    const valid = selectedDeals.filter((d) => d.amountToSell && Number(d.amountToSell) > 0);
    if (!valid.length) {
      setError('Please select at least one deal and enter a valid amount to sell.');
      return;
    }
    if (!balanceValidation.isValid) {
      setError(`Balance validation failed: ${balanceValidation.message}`);
      return;
    }
    if (!checkAmountDifference()) return;
    finishConfirm(undefined);
  };

  const handleContinueWithDifference = () => {
    setShowAmountWarning(false);
    const totalSellAmount = selectedDeals.reduce((sum, d) => sum + (Number(d.amountToSell) || 0), 0);
    finishConfirm(totalSellAmount);
  };

  if (!show) return null;

  return (
    <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', overflowY: 'auto', zIndex: 1050 }}>
      <div className="modal" style={{ display: 'block', zIndex: 1100, maxWidth: 1100, margin: '40px auto', width: '90vw', position: 'relative', background: '#ffffff', borderRadius: 8, boxShadow: '0 10px 40px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
        <div className="modal-header">
          <h5 className="modal-title">Select T-Bill Holdings to Sell</h5>
          <button type="button" className="close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-danger">{error}</div>}

          {!balanceValidation.isValid && (
            <div className="alert alert-warning">
              <strong>Balance validation failed:</strong> {balanceValidation.message}
            </div>
          )}

          {tbillFaceValue ? (
            <div
              style={{
                padding: '12px 16px',
                marginBottom: 16,
                background: '#fff7d6',
                border: '1px solid #f1b500',
                borderLeft: '6px solid #f1b500',
                borderRadius: 8
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#8a6300' }}>
                Target face value to sell
              </div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#7a4f00' }}>
                {Number(tbillFaceValue).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </div>
              {pricePer100 ? (
                <div style={{ fontSize: 12, color: '#7a5800', marginTop: 4 }}>
                  Sell price per 100: <strong>{pricePer100}</strong>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="form-group">
            <label>Portfolio</label>
            <select className="form-control" value={selectedPortfolio} onChange={(e) => handlePortfolioChange(e.target.value)}>
              <option value="">Select Portfolio</option>
              {portfolios.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Search (Deal # or discount rate)</label>
            <input
              className="form-control"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type to filter..."
            />
          </div>

          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table className="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Select</th>
                  <th>Deal #</th>
                  <th>ISIN</th>
                  <th>Discount %</th>
                  <th>Face Value</th>
                  <th>Remaining FV</th>
                  <th>Value Date</th>
                  <th>Amount to Sell</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8}>Loading...</td>
                  </tr>
                ) : filteredDeals.length === 0 ? (
                  <tr>
                    <td colSpan={8}>No eligible T-Bill buy deals found.</td>
                  </tr>
                ) : (
                  filteredDeals.map((deal) => {
                    const selected = selectedDeals.some((d) => d.dealId === deal.id);
                    const amountToSell = selectedDeals.find((d) => d.dealId === deal.id)?.amountToSell || '';
                    const maxVal = deal.remaining_face_value || deal.face_value;
                    return (
                      <tr key={deal.id}>
                        <td>
                          <input type="checkbox" checked={selected} onChange={() => handleDealSelect(deal.id)} />
                        </td>
                        <td>{deal.deal_number}</td>
                        <td>{deal.isin}</td>
                        <td>{deal.yield ?? deal.discount_rate_pct ?? '—'}</td>
                        <td>{Number(deal.face_value || 0).toLocaleString()}</td>
                        <td>{Number(deal.remaining_face_value || 0).toLocaleString()}</td>
                        <td>{formatDate(deal.value_date)}</td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            max={maxVal}
                            value={amountToSell}
                            onChange={(e) => handleAmountChange(deal.id, e.target.value, maxVal)}
                            disabled={!selected}
                            className="form-control form-control-sm"
                            style={{ width: 120 }}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={7} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                    Total
                  </td>
                  <td style={{ fontWeight: 'bold' }}>
                    {selectedDeals.reduce((sum, d) => sum + (Number(d.amountToSell) || 0), 0).toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleConfirm}>
            Confirm Selection
          </button>
        </div>
      </div>

      {showAmountWarning && (
        <div className="modal-backdrop" style={{ zIndex: 1200 }}>
          <div className="modal" style={{ display: 'block', zIndex: 1250, maxWidth: 480, margin: '80px auto' }}>
            <div className="modal-header">
              <h5 className="modal-title">Amount Mismatch</h5>
            </div>
            <div className="modal-body">
              <p>
                The total selected sell amount differs from the form face value by{' '}
                <strong>{amountDifference.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>.
              </p>
              <p>Continue and update the form face value to match the selected total?</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowAmountWarning(false)}>
                Adjust Amounts
              </button>
              <button type="button" className="btn btn-primary" onClick={handleContinueWithDifference}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

TbillSellDealModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onPortfolioChange: PropTypes.func,
  isin: PropTypes.string,
  tbillFaceValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pricePer100: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  formValueDate: PropTypes.string
};
